import type { ProviderName } from '../interfaces/IAuth.js';
import type { MonitorSnapshot } from '../interfaces/IMonitorApi.js';
import type { IMonitorReplay } from '../interfaces/IMonitorReplay.js';
import { ProviderErrorResponse, UnauthorizedError } from './errors.js';
import type { AuthedFetch } from './transport.js';

export class SiteNotLinkedError extends Error {
  constructor(public readonly siteId: string) {
    super(`site ${siteId} has no trusstor project linked`);
    this.name = 'SiteNotLinkedError';
  }
}

export class UpstreamError extends Error {
  constructor() { super('upstream provider failed'); this.name = 'UpstreamError'; }
}

export async function parseStandardErrors(res: Response): Promise<never> {
  const body = await res.json().catch(() => null) as Record<string, unknown> | null;
  const code: string | undefined = (body?.error as Record<string, unknown> | undefined)?.code as string | undefined;

  // 401 reaches us only after authedFetch's refresh-and-retry has already
  // failed and dispatched `dorca:auth-expired`; we just surface the error
  // type so React Query lands in an error state until the redirect fires.
  if (res.status === 401) throw new UnauthorizedError();
  if (
    res.status === 409 &&
    (code === 'PROVIDER_NOT_CONNECTED' || code === 'PROVIDER_DISCONNECTED')
  ) {
    const errorDetails = (body?.error as Record<string, unknown> | undefined)?.details as Record<string, unknown> | undefined;
    throw new ProviderErrorResponse(code, errorDetails?.provider as ProviderName);
  }
  if (res.status === 409 && code === 'SITE_NOT_LINKED') {
    const errorDetails = (body?.error as Record<string, unknown> | undefined)?.details as Record<string, unknown> | undefined;
    throw new SiteNotLinkedError((errorDetails?.siteId as string | undefined) ?? '');
  }
  if (res.status === 502) throw new UpstreamError();

  const errorMsg = (body?.error as Record<string, unknown> | undefined)?.message as string | undefined;
  throw new Error(`request failed: ${res.status} ${errorMsg ?? ''}`);
}

/**
 * The monitor API over an injected transport — each app binds its own
 * `authedFetch` in its `apis/monitor.ts`.
 */
export function createMonitorApi(authedFetch: AuthedFetch) {
  async function fetchMonitorSnapshot(
    siteId: string,
    signal?: AbortSignal,
  ): Promise<MonitorSnapshot> {
    const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/monitor/snapshot`, { signal });
    if (res.ok) return (await res.json()) as MonitorSnapshot;
    return parseStandardErrors(res);
  }

  async function fetchMonitorReplay(
    siteId: string,
    date: string,
    signal?: AbortSignal,
  ): Promise<IMonitorReplay> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/monitor/replay?date=${encodeURIComponent(date)}`,
      { signal },
    );
    if (res.ok) return (await res.json()) as IMonitorReplay;
    return parseStandardErrors(res);
  }

  return { fetchMonitorSnapshot, fetchMonitorReplay };
}
